import { FiSearch } from "react-icons/fi";
import { useState, useEffect } from "react";
import { IoChatbubbleEllipsesOutline } from "react-icons/io5";
import { AiOutlineArrowRight } from "react-icons/ai";
import { IoIosArrowBack } from "react-icons/io";
import { IoIosArrowForward } from "react-icons/io";
import { GrAddCircle } from "react-icons/gr";
import { IoCalendarSharp } from "react-icons/io5";
import { PiClockFill } from "react-icons/pi";
import { PiFileTextBold } from "react-icons/pi";
import { MdArrowBackIos } from "react-icons/md";
import { FaEdit } from "react-icons/fa";
import styles from "./CaseNotes.module.css";
import { sendCaseNotes } from "actions/bookSlot";
import { useUserTokenSelector } from "store/reducers";
import { getClientCaseNotes } from "actions/dataLoad";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading } from "store/actions";

const Casenotes = ({ onCasenoteBack, client, id, view, caseNote }) => {
    const date = new Date();
    const [caseNotes, setCaseNotes] = useState(caseNote || []); // Initialize with prop or empty array
    const [editingSymptoms, setEditingSymptoms] = useState(false);
    const [editedSymptoms, setEditedSymptoms] = useState(client?.symptoms?.join(", ") || "");
    const [clientData, setClientData] = useState(client);
    const [viewnote, setViewnote] = useState(view);
    const [therapyGoals, setTherapyGoals] = useState();
    const [observations, setObservations] = useState();
    const [file, setFile] = useState(null);
    const [fileNameForDisplay, setFileNameForDisplay] = useState(""); // New state for displaying file name
    const avatar = {
        female: "https://ucarecdn.com/c22b8f55-8c68-411e-a5c9-8b5b99c08701/femaleEmoji.svg",
        male: "https://ucarecdn.com/52c99efd-cba4-4fe1-93f2-e393088b7a0c/male.svg",
        other: "https://ucarecdn.com/c7bb4c01-2258-428d-b8ce-e2196ee63220/lgbtq.svg",
    };
    const token = useUserTokenSelector();
    const dispatch = useDispatch();

    // Initialize index. If viewing a specific note, find its index.
    // If submitting new, index can start at 0 or last note for when transitioning to view.
    const initialIndex = () => {
        if (view && id && typeof id === 'object' && caseNotes && caseNotes.length > 0) {
            const foundIndex = caseNotes.findIndex(note => note._id === id._id);
            return foundIndex !== -1 ? foundIndex : 0; // Default to 0 if not found
        }
        // If not viewing a specific note (i.e., view is false, or id is not a specific note object)
        // or if caseNotes is empty, default to 0.
        // When transitioning from submit to view, we'll set index to last note.
        return 0;
    };
    const [index, setIndex] = useState(initialIndex());

    useEffect(() => {
        // Update local caseNotes state if the prop changes
        setCaseNotes(caseNote || []);
    }, [caseNote]);

    const handleBack = () => {
        onCasenoteBack();
    }
    const handleEditSymptoms = () => {
        setEditingSymptoms(true);
    };
    const handleDecrease = () => {
        if (!viewnote) { // Currently in "submit new note" mode
            if (caseNotes && caseNotes.length > 0) {
                setIndex(caseNotes.length - 1); // Go to the last existing note
                setViewnote(true); // Switch to view mode
            }
        } else { // Currently in "view existing note" mode
            if (index > 0) {
                setIndex(index - 1);
            }
        }
    }
    const handleIncrease = () => {
        if (viewnote) { // Currently in "view existing note" mode
            if (index < caseNotes.length - 1) {
                setIndex(index + 1);
            } else if (index === caseNotes.length - 1 && !view) { 
                // At the last note AND initial mode was to submit (view prop was false)
                setViewnote(false); // Switch back to "submit new note" mode
                // Index becomes irrelevant for display, could reset or leave as is
            }
        }
        // No action for right arrow if !viewnote (in submit new note mode)
    }
    const handleSaveSymptoms = () => {
        const updatedSymptoms = editedSymptoms?.split?.(", ");
        const updatedClient = { ...clientData, symptoms: updatedSymptoms };
        setClientData(updatedClient);
        setEditingSymptoms(false);
    };
    const handleFileInputChange = (e) => {
        const selectedFile = e.target.files[0];
        // if (file) {
        //     setFile(file.name);
        // }
        if (selectedFile) {
            setFileNameForDisplay(selectedFile.name); // Store the name for display
            const reader = new FileReader();
            reader.onloadend = () => {
                setFile(reader.result); // Store base64 string for submission
            };
            reader.readAsDataURL(selectedFile);
        } else {
            setFile(null);
            setFileNameForDisplay("");
        }
    }
    const handleSubmitCaseNote = async () => {
        // When submitting, 'id' prop should be the session object or its ID
        const bookingID = typeof id === 'object' ? id._id : id; 
        if (!bookingID) {
            console.error("Booking ID is missing for case note submission.");
            // Handle error: show a message to the user
            return;
        }
        dispatch(startLoading())
        const payload = {
            bookingID: bookingID,
            goals: therapyGoals || "", 
            keyobservation: observations || "", 
            tasks: observations || "", // Placeholder or derive from somewhere
            keyRecommendation: "", // Placeholder
            caseNotes: observations || "", 
            image: file, // This should be the base64 string
        };

        try {
            const response = await sendCaseNotes(payload, token); 
            
            const newNote = response.data?.caseNote || { 
                ...payload, 
                _id: response.data?.caseNote?._id || `temp-${Date.now()}`, 
                CaseNotesUploadTimestamp: response.data?.caseNote?.CaseNotesUploadTimestamp || new Date().toISOString(),
                // Ensure all relevant fields displayed in Client.js are present
                // 'keyobservation' from payload is already there.
            };
            onCasenoteBack(newNote); // Pass the new note back to Client.js
            // Clear form fields
            setTherapyGoals(""); 
            setObservations(""); 
            setFile(null);
            setFileNameForDisplay("");
        } catch (error) {
            console.error("Error submitting case note:", error);
            // alert("Failed to submit case note."); // Or use a more sophisticated notification
        } finally {
            dispatch(stopLoading())
        }
    }
    const handleChange = (e) => {
        setEditedSymptoms(e.target.value);
    };
    // Determine the note to display based on viewnote state and index
    // If id is provided and view is true, it means we are viewing a specific note passed by parent.
    // However, navigation should then use the 'index' relative to 'caseNotes' array.
    // The initial 'id' prop is mainly for setting the starting 'index' if 'view' is true.
    const currentNoteToDisplay = viewnote && caseNotes && caseNotes.length > 0 ? caseNotes[index] : null;

    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";
        const dateObj = new Date(timestamp);
        if (isNaN(dateObj.getTime())) return "Invalid Date";
        return dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    return (
        <>
            <div className={styles['client-top']}>
                <div className={styles['client-close']} onClick={handleBack}>
                    <MdArrowBackIos size={18} style={{ marginLeft: "8px" }} />
                </div>
                <div className={styles['client-profile']}>
                    <img
                        src={avatar[clientData?.gender]}
                        alt="user-avatar"
                        className={styles['client-img']}
                    />
                    <div className={styles['profile-rt']}>
                        <div className={styles['profile-rtt']}>
                            <p className={styles['client-heading']}>{clientData?.name}</p>
                            <div className={styles['cchat-div']}>
                                <IoChatbubbleEllipsesOutline size={20} color="white" />
                            </div>
                        </div>
                        <div className={styles['profile-sum']}>
                            <p>Problems: {(clientData.pastSession || clientData.upcomingSession)?.problemCategory?.length === 0 ? (
                                <span>-</span>
                            ) : !editingSymptoms && (
                                (clientData.pastSession || clientData.upcomingSession)?.problemCategory?.map?.((symptom, index) => (
                                    <span key={index}>{symptom}{(clientData.pastSession || clientData.upcomingSession)?.problemCategory.length - 1 > index && ", "}</span>
                                ))
                            )}
                                {editingSymptoms ? (
                                    <>
                                        <input type="text" value={editedSymptoms} onChange={handleChange} className={styles['sedit']} />
                                        <button onClick={handleSaveSymptoms} className={styles['sedit-btn']}>Save</button>
                                    </>
                                ) : (
                                    <button className={styles['edit-sbtn']}>
                                        <FaEdit onClick={handleEditSymptoms} />
                                    </button>
                                )}
                            </p>
                            <p>Last Session Date: {new Date((clientData.pastSession || clientData.upcomingSession)?.sessionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/ /g, ' ').replace(/ /, ', ')}</p>
                            <p>Problem description: {(clientData.pastSession)?.problemDescription || (clientData.upcomingSession)?.problemDescription}</p>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles['casenote-start']}>
                <p className={`${styles['cn-head']} ${styles['dt']}`}>Session Date & Time</p>
                <div className={styles['cn-top']}>
                    <div 
                        className={`${styles['cn-icon']} ${(!viewnote && (!caseNotes || caseNotes.length === 0)) || (viewnote && index === 0) ? styles['disable'] : ''}`} 
                        onClick={handleDecrease}
                    >
                        <IoIosArrowBack />
                    </div>
                    <div className={styles['cn-datetime']}>
                        <div className={styles['datetime-div']}>
                            <IoCalendarSharp color={"#49AF7C"} size={20} />
                            <p>{viewnote ? formatDate(currentNoteToDisplay?.CaseNotesUploadTimestamp) : formatDate(new Date().toISOString())}</p>
                        </div>
                        <div div className={styles['datetime-div']}>
                            <PiClockFill color={"#49AF7C"} size={20} />
                            <p>{viewnote ? (currentNoteToDisplay?.CaseNotesUploadTimestamp ? new Date(currentNoteToDisplay.CaseNotesUploadTimestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase() : "N/A") : new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase()}</p>
                        </div>
                    </div>
                    <div 
                        className={`${styles['cn-icon']} ${!viewnote || (viewnote && index === caseNotes.length - 1 && view) ? styles['disable'] : ''}`} 
                        onClick={handleIncrease}
                    >
                        {/* Disable right arrow if in submit mode, or if viewing last note AND initial mode was to view a specific note */}
                        <IoIosArrowForward />
                    </div>
                </div>
                <div className={styles['cn-mid']}>
                    <div className={styles['cnmid-div']}>
                        <p className={styles['cn-head']}>Threapy Goals</p>
                        <div className={styles['mid-div']}>
                            {
                                viewnote ?
                                    <div className={styles['mid-txt']}>
                                        <p>
                                            {currentNoteToDisplay?.goals || "No goal specified"}
                                        </p>
                                    </div>
                                    : <textarea className={styles['casenote-input']} onChange={(e) => { setTherapyGoals(e.target.value) }} />
                            }
                        </div>
                    </div>
                    <div className={styles['cnmid-div']}>
                        <p className={styles['cn-head']}>Key Observations</p>
                        <div className={styles['mid-div']}>
                            {
                                viewnote ?
                                    <ul>
                                        {
                                            (currentNoteToDisplay?.keyobservation || currentNoteToDisplay?.caseNotes || "").split('\n').map((observation, obsIndex) => {
                                                return (
                                                    <li key={index}>
                                                        {observation}
                                                    </li>
                                                );
                                            })
                                        }
                                    </ul>
                                    : <textarea className={styles['casenote-input']} onChange={(e) => { setObservations(e.target.value) }} />
                            }
                        </div>
                    </div>
                </div>
                <div className={styles['cn-bottom']}>
                    <div className={styles['cnbottom-div']}>
                        <p className={styles['cn-head']}>Add Attachments</p>
                        <div className={styles['bottom-div']}>
                            {
                                viewnote ?
                                    <>
                                        <PiFileTextBold size={20} color="#49AF7C" />
                                        {currentNoteToDisplay?.imageUrl
                                            ? <p><a href={currentNoteToDisplay.imageUrl} target="_blank" rel="noopener noreferrer">View Attachment</a></p> 
                                            : <p>No file attached</p>}
                                    </>
                                    :
                                    <>
                                        <label htmlFor="file-upload">
                                            <PiFileTextBold size={20} color="#49AF7C" />
                                        </label>
                                        <input
                                            id="file-upload"
                                            type="file"
                                            onChange={handleFileInputChange}
                                            style={{ display: "none" }}
                                        />
                                        {fileNameForDisplay && <p>{fileNameForDisplay}</p>}
                                    </>
                            }
                        </div>
                    </div>
                    {
                        !viewnote &&
                        <button onClick={handleSubmitCaseNote} className={styles['casenote-submit']}>
                            Submit
                        </button>
                    }
                </div>
            </div>
        </>
    );
};

export default Casenotes;
